import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Divider,
    TextField,
    Autocomplete,
    Chip,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";
import { ExpandMore, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

import queryGraphql from "../../../helpers/ApiInterface";
import MainDrawer from "../../MainDrawer";
import MiniBot from "../../MiniBot";
import FineTuningRequestsTable from "./FineTuningRequestsTable"; // Import the new table

// Interface for search options, tailored for Fine-Tuning Jobs
interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: string;
    groupBy: string;
}

// Interface for the processed job data structure used in this page
interface ProcessedFineTuningJob {
    id: string;
    jobName: string;
    applicationName?: string;
    status: string;
    createdAt: string | null;
    actionedAt: string | null;
    jobOwnerName: string | null | undefined;
    jobOwnerAvatar?: string;
    jobApproverName: string | null | undefined;
    jobApproverAvatar?: string;
    reasonForRejection?: string;
}

const FineTuningRequestsPage: React.FC = () => {
    const [jobs, setJobs] = useState<ProcessedFineTuningJob[]>([]);
    const [searchOptions, setSearchOptions] = useState<SearchOptions[]>([]);
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                // GraphQL Query to get fine-tuning jobs
                const data = await queryGraphql(`
                    query GetFineTunings {
                      getFineTunings {
                        id
                        name
                        application {
                           id
                           applicationName
                        }
                        status
                        reasonForRejection
                        createdAt
                        updatedAt
                        solutionOwner {
                          id
                          name
                          googleProfilePicture
                        }
                        solutionApprover {
                          id
                          name
                          googleProfilePicture
                        }
                      }
                    }
                `);

                const rawJobsData = data.getFineTunings || [];

                // Process the raw data into the structure needed for the requests view
                const processedJobsArr: ProcessedFineTuningJob[] = rawJobsData.map((job: any) => ({
                    id: job.id,
                    jobName: job.name,
                    applicationName: job.application?.applicationName,
                    status: job.status ? job.status.toLowerCase() : 'pending',
                    createdAt: job.createdAt ? new Date(Number(job.createdAt)).toISOString() : null,
                    actionedAt: job.updatedAt ? new Date(Number(job.updatedAt)).toISOString() : null,
                    jobOwnerName: job.solutionOwner?.name,
                    jobOwnerAvatar: job.solutionOwner?.googleProfilePicture,
                    jobApproverName: job.solutionApprover?.name,
                    jobApproverAvatar: job.solutionApprover?.googleProfilePicture,
                    reasonForRejection: job.reasonForRejection,
                }));
                setJobs(processedJobsArr);

                // Generate search options from the processed job data
                let generatedOptions: SearchOptions[] = processedJobsArr
                    .map((job: ProcessedFineTuningJob) => {
                        const options = [
                            {
                                category: "jobName",
                                groupBy: "JOB NAME",
                                chipLabel: `Job: ${job.jobName}`,
                                dropdownLabel: job.jobName,
                            },
                        ];
                        if (job.applicationName) {
                            options.push({
                                category: "applicationName",
                                groupBy: "APPLICATION",
                                chipLabel: `App: ${job.applicationName}`,
                                dropdownLabel: job.applicationName,
                            });
                        }
                        if (job.jobOwnerName) {
                            options.push({
                                category: "jobOwnerName",
                                groupBy: "OWNER",
                                chipLabel: `Owner: ${job.jobOwnerName}`,
                                dropdownLabel: job.jobOwnerName,
                            });
                        }
                        if (job.jobApproverName) {
                            options.push({
                                category: "jobApproverName",
                                groupBy: "APPROVER",
                                chipLabel: `Approver: ${job.jobApproverName}`,
                                dropdownLabel: job.jobApproverName,
                            });
                        }
                        if (job.status) {
                            options.push({
                                category: "status",
                                groupBy: "STATUS",
                                chipLabel: `Status: ${job.status.charAt(0).toUpperCase() + job.status.slice(1)}`,
                                dropdownLabel: job.status.charAt(0).toUpperCase() + job.status.slice(1),
                            });
                        }
                        return options;
                    })
                    .flat();

                // Deduplicate search options
                generatedOptions = generatedOptions.filter(
                    (obj, index, self) =>
                        obj.dropdownLabel &&
                        index === self.findIndex((t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel)
                );

                // Sort search options
                generatedOptions.sort((a, b) => {
                    if (a.groupBy > b.groupBy) return 1;
                    if (a.groupBy < b.groupBy) return -1;
                    if (a.dropdownLabel > b.dropdownLabel) return 1;
                    if (a.dropdownLabel < b.dropdownLabel) return -1;
                    return 0;
                });
                setSearchOptions(generatedOptions);

            } catch (error) {
                console.log("Error fetching fine-tuning jobs for request page:", error);
            }
        };

        fetchData();
    }, []);

    // Filter jobs based on the search query
    const filteredJobs =
        searchQuery.length === 0
            ? jobs
            : jobs.filter(
                (job: ProcessedFineTuningJob) =>
                    (searchQuery.filter((e) => e.category === "jobName").length > 0
                        ? searchQuery
                                .filter((e) => e.category === "jobName")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(job.jobName?.toLowerCase())
                        : true) &&
                    (searchQuery.filter((e) => e.category === "applicationName").length > 0
                        ? (job.applicationName &&
                            searchQuery
                                .filter((e) => e.category === "applicationName")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(job.applicationName.toLowerCase()))
                        : true) &&
                    (searchQuery.filter((e) => e.category === "jobOwnerName").length > 0
                        ? (job.jobOwnerName &&
                            searchQuery
                                .filter((e) => e.category === "jobOwnerName")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(job.jobOwnerName.toLowerCase()))
                        : true
                    ) &&
                    (searchQuery.filter((e) => e.category === "jobApproverName").length > 0
                        ? (job.jobApproverName &&
                            searchQuery
                                .filter((e) => e.category === "jobApproverName")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(job.jobApproverName.toLowerCase()))
                        : true
                    ) &&
                    (searchQuery.filter((e) => e.category === "status").length > 0
                        ? searchQuery
                                .filter((e) => e.category === "status")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(job.status?.toLowerCase())
                        : true)
            );

    // Group filtered jobs by status for accordions
    const pendingStatuses = ['pending', 'submitted', 'provisioning'];
    const activeStatuses = ['active', 'approved', 'success'];
    const rejectedStatuses = ['rejected', 'failed'];

    const filteredPendingJobs = filteredJobs.filter(
        (job: ProcessedFineTuningJob) => pendingStatuses.includes(job.status)
    );
    const filteredActiveJobs = filteredJobs.filter(
        (job: ProcessedFineTuningJob) => activeStatuses.includes(job.status)
    );
    const filteredRejectedJobs = filteredJobs.filter(
        (job: ProcessedFineTuningJob) => rejectedStatuses.includes(job.status)
    );

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"requests-finetuning"} />
            <MiniBot />
            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#fafafa",
                    minHeight: "100vh",
                }}
            >
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={2} mb="18px">
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginY: 1,
                                marginLeft: 2,
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
                                Requests
                            </Typography>
                             <IconButton
                                aria-label="Help"
                                onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                sx={{
                                    padding: 0, border: 'none',  mr:2,
                                    '&:hover': { backgroundColor: 'action.hover' },
                                }}
                              >
                                <Box component="img" src="/assets/Help.svg" alt="Help Icon" sx={{ width: 40, height: 40, display: 'block' }} />
                            </IconButton>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box
                            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}
                        >
                            <Box>
                                <Typography
                                    fontFamily="Gilroy-SemiBold"
                                    fontSize="16px"
                                >{`FINE TUNING JOBS (${jobs.length})`}</Typography>
                                <Typography
                                    fontFamily="Gilroy-Regular"
                                    fontSize="14px"
                                >{`View all the requests raised for Fine Tuning Jobs`}</Typography>
                            </Box>
                            <Autocomplete
                                multiple
                                options={searchOptions}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                value={searchQuery}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) => (
                                    tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />
                                    ))
                                )}
                                onChange={(e, newVal) => {
                                    setSearchQuery(newVal);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by job, owner, status etc"
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <img
                                                            src="/assets/search-normal.svg"
                                                            alt="Search Icon"
                                                            style={{ width: "16px", paddingLeft: "4px" }}
                                                        />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: {
                                                backgroundColor: "#f4f4f4",
                                                minHeight: "45px",
                                                width: "500px",
                                                borderRadius: "8px",
                                                fontFamily: "Gilroy-Regular",
                                                fontSize: "14px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                    <Box m={2}>
                        <Accordion
                            defaultExpanded={filteredPendingJobs.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredPendingJobs.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }}/>
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Pending Jobs ({filteredPendingJobs.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredPendingJobs.length > 0 && (
                                    <FineTuningRequestsTable jobs={filteredPendingJobs} />
                                )}
                            </AccordionDetails>
                        </Accordion>
                        
                        <Accordion
                            defaultExpanded={true}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredActiveJobs.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Active Jobs ({filteredActiveJobs.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredActiveJobs.length > 0 && (
                                    <FineTuningRequestsTable jobs={filteredActiveJobs} />
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={filteredRejectedJobs.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredRejectedJobs.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Rejected Jobs ({filteredRejectedJobs.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredRejectedJobs.length > 0 && (
                                    <FineTuningRequestsTable jobs={filteredRejectedJobs} />
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default FineTuningRequestsPage;